import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiX, FiHome, FiUsers, FiSettings, FiBarChart2, FiFileText, FiShoppingCart, FiDollarSign, FiTrendingUp, FiLogOut, FiUserCheck, FiBriefcase, FiChevronDown, FiChevronRight, FiCreditCard, FiCalendar, FiInfo, FiImage } from 'react-icons/fi';
import { FaGraduationCap } from "react-icons/fa";
import logo from '../components/Images/logo-green.svg';

const Sidebar = ({ isOpen, onClose, user = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [homeDropdownOpen, setHomeDropdownOpen] = useState(false);
  const [cmsDropdownOpen, setCmsDropdownOpen] = useState(false);
  const [studentsDropdownOpen, setStudentsDropdownOpen] = useState(false);
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);
  
  // Determine active menu based on current location
  const getActiveMenu = () => {
    if (location.pathname === '/dashboard') return 'dashboard';
    if (location.pathname.startsWith('/dashboard/home')) return 'home';
    if (location.pathname.startsWith('/dashboard/home/home-banners')) return 'home-banners';
    if (location.pathname.startsWith('/dashboard/home/what-we-are')) return 'what-we-are';
    if (location.pathname.startsWith('/dashboard/about')) return 'about';
    if (location.pathname.startsWith('/dashboard/our-clients')) return 'clients';
    if (location.pathname.startsWith('/dashboard/companies')) return 'companies';
    if (location.pathname.startsWith('/dashboard/company')) return 'company';
    if (location.pathname.startsWith('/dashboard/students-list')) return 'students-list';
    if (location.pathname.startsWith('/dashboard/students')) return 'students';
    if (location.pathname.startsWith('/dashboard/cms-pages')) return 'cms';
    if (location.pathname.startsWith('/dashboard/admins')) return 'admins';
    if (location.pathname.startsWith('/dashboard/settings')) return 'settings';
    if (location.pathname.startsWith('/dashboard/transactions')) return 'transactions';
    if (location.pathname.startsWith('/dashboard/events')) return 'events';
    if (location.pathname.startsWith('/dashboard/event-requests')) return 'event-requests';
    if (location.pathname.startsWith('/dashboard/subscriptions')) return 'subscriptions';
    if (location.pathname.startsWith('/dashboard/user-subscriptions')) return 'user-subscriptions';
    if (location.pathname.startsWith('/dashboard/contact-us')) return 'contact-us';
    if (location.pathname.startsWith('/dashboard/interests')) return 'interests';
    if (location.pathname.startsWith('/dashboard/nationalities')) return 'nationalities';
    if (location.pathname.startsWith('/dashboard/system-images')) return 'system-images';
    return 'dashboard';
  };
  
  const [activeMenu, setActiveMenu] = useState(getActiveMenu());

  const menuItems = [
    // Main Navigation Section
    { 
      key: 'dashboard', 
      label: 'Dashboard', 
      icon: <FiHome className="h-5 w-5" />, 
      href: '/dashboard' 
    },
    
    
    // Additional Features Section
    { 
      key: 'companies', 
      label: 'Companies List', 
      icon: <FiBriefcase className="h-5 w-5" />, 
      href: '/dashboard/companies' 
    },
    { 
      key: 'students-list', 
      label: 'Students List', 
      icon: <FaGraduationCap className="h-5 w-5" />, 
      href: '/dashboard/students-list' 
    },
    { 
      key: 'interests', 
      label: 'Interests', 
      icon: <FiBarChart2 className="h-5 w-5" />, 
      href: '/dashboard/interests' 
    },
    { 
      key: 'nationalities', 
      label: 'Nationalities', 
      icon: <FiUsers className="h-5 w-5" />, 
      href: '/dashboard/nationalities' 
    },
    { 
      key: 'subscriptions', 
      label: 'Subscriptions', 
      icon: <FiDollarSign className="h-5 w-5" />, 
      href: '/dashboard/subscriptions' 
    },
    { 
      key: 'user-subscriptions', 
      label: 'Companies Subscriptions', 
      icon: <FiDollarSign className="h-5 w-5" />, 
      href: '/dashboard/user-subscriptions' 
    },
    { 
      key: 'transactions', 
      label: 'Transactions', 
      icon: <FiCreditCard className="h-5 w-5" />, 
      href: '/dashboard/transactions' 
    },
    { 
      key: 'events', 
      label: 'Events', 
      icon: <FiCalendar className="h-5 w-5" />, 
      href: '/dashboard/events' 
    },
    { 
      key: 'event-requests', 
      label: 'Event Requests', 
      icon: <FiCalendar className="h-5 w-5" />, 
      href: '/dashboard/event-requests' 
    },
    // Content Management Section
    { 
      key: 'home', 
      label: 'Home', 
      icon: <FiHome className="h-5 w-5" />, 
      isDropdown: true,
      subItems: [
        { key: 'home-banners', label: 'Home Banner', href: '/dashboard/home/home-banners' },
        { key: 'what-we-are', label: 'What We Are', href: '/dashboard/home/what-we-are' }
      ]
    },
    { 
      key: 'about', 
      label: 'About', 
      icon: <FiFileText className="h-5 w-5" />, 
      isDropdown: true,
      subItems: [
        { key: 'about-page', label: 'About', href: '/dashboard/about/about' },
        { key: 'what-we-are', label: 'What We Are', href: '/dashboard/about/what-we-offer' }
      ]
    },
    { 
      key: 'clients', 
      label: 'Our Clients', 
      icon: <FiBriefcase className="h-5 w-5" />, 
      href: '/dashboard/our-clients' 
    },
    { 
      key: 'company', 
      label: 'Company Page', 
      icon: <FiBriefcase className="h-5 w-5" />, 
      isDropdown: true,
      subItems: [
        { key: 'company-how-to-apply', label: 'How to Apply', href: '/dashboard/company/how-to-apply' },
        { key: 'company-what-we-offer', label: 'What We Offer', href: '/dashboard/company/what-we-offer' }
      ]
    },
    { 
      key: 'students', 
      label: 'Students Page', 
      icon: <FaGraduationCap className="h-5 w-5" />, 
      isDropdown: true,
      subItems: [
        { key: 'how-to-apply', label: 'How to Apply', href: '/dashboard/students/how-to-apply' },
        { key: 'what-we-offer', label: 'What We Offer', href: '/dashboard/students/what-we-offer' }
      ]
    },
    { 
      key: 'cms', 
      label: 'CMS Pages', 
      icon: <FiFileText className="h-5 w-5" />, 
      isDropdown: true,
      subItems: [
        { key: 'terms', label: 'Terms', href: '/dashboard/cms-pages/terms' },
        { key: 'privacy', label: 'Privacy', href: '/dashboard/cms-pages/privacy' },
        { key: 'faqs', label: 'FAQs', href: '/dashboard/cms-pages/faqs' },
      ]
    },
    { 
      key: 'system-images', 
      label: 'System Images', 
      icon: <FiImage className="h-5 w-5" />, 
      href: '/dashboard/system-images' 
    },
    { 
      key: 'contact-us', 
      label: 'Contact Us', 
      icon: <FiInfo className="h-5 w-5" />, 
      href: '/dashboard/contact-us' 
    },
    { 
      key: 'admins', 
      label: 'Admins', 
      icon: <FiUserCheck className="h-5 w-5" />, 
      href: '/dashboard/admins' 
    },
    { 
      key: 'settings', 
      label: 'Settings', 
      icon: <FiSettings className="h-5 w-5" />, 
      href: '/dashboard/settings' 
    },
  ];

  const handleMenuClick = (menuKey, href) => {
    if (menuKey === 'home') {
      setHomeDropdownOpen(!homeDropdownOpen);
      return;
    }
    if (menuKey === 'cms') {
      setCmsDropdownOpen(!cmsDropdownOpen);
      return;
    }
    if (menuKey === 'students') {
      setStudentsDropdownOpen(!studentsDropdownOpen);
      return;
    }
    if (menuKey === 'company') {
      setCompanyDropdownOpen(!companyDropdownOpen);
      return;
    }
    if (menuKey === 'about') {
      setAboutDropdownOpen(!aboutDropdownOpen);
      return;
    }
    setActiveMenu(menuKey);
    navigate(href);
    onClose(); // Close mobile sidebar when menu item is clicked
  };

  const handleSubMenuClick = (href) => {
    navigate(href);
    onClose(); // Close mobile sidebar when submenu item is clicked
  };

  const isActiveHomePage = () => {
    return location.pathname.startsWith('/dashboard/home');
  };

  const isActiveCmsPage = () => {
    return location.pathname.startsWith('/dashboard/cms-pages');
  };

  const isActiveStudentsPage = () => {
    return location.pathname.startsWith('/dashboard/students') && !location.pathname.startsWith('/dashboard/students-list');
  };

  const isActiveCompanyPage = () => {
    return location.pathname.startsWith('/dashboard/company');
  };

  const isActiveAboutPage = () => {
    return location.pathname.startsWith('/dashboard/about');
  };

  const handleLogout = () => {
    // Clear all localStorage data
    localStorage.clear();
    // Navigate to login page
    window.location.href = '/login';
  };

  // Function to render menu item with optional border
  const renderMenuItem = (item, index, showBorder = false) => (
    <li key={item.key}>
      {showBorder && index > 0 && (
        <div className="border-t border-secondary-700 my-3"></div>
      )}
      {item.isDropdown ? (
        <div>
          <button
            onClick={() => handleMenuClick(item.key, item.href)}
            className={`
              w-full flex items-center justify-between px-2 py-3 text-sm font-medium rounded-lg transition-colors duration-200
              ${(item.key === 'home' && isActiveHomePage()) || (item.key === 'cms' && isActiveCmsPage()) || (item.key === 'students' && isActiveStudentsPage()) || (item.key === 'company' && isActiveCompanyPage()) || (item.key === 'about' && isActiveAboutPage())
                ? 'bg-primary-500 text-white shadow-md'
                : 'text-gray-300 hover:bg-secondary-800 hover:text-white'
              }
            `}
          >
            <div className="flex items-center">
              <span className="mr-3">{item.icon}</span>
              {item.label}
            </div>
            {(item.key === 'home' && homeDropdownOpen) || (item.key === 'cms' && cmsDropdownOpen) || (item.key === 'students' && studentsDropdownOpen) || (item.key === 'company' && companyDropdownOpen) || (item.key === 'about' && aboutDropdownOpen) ? 
              <FiChevronDown className="h-4 w-4" /> : 
              <FiChevronRight className="h-4 w-4" />
            }
          </button>
          {((item.key === 'home' && homeDropdownOpen) || (item.key === 'cms' && cmsDropdownOpen) || (item.key === 'students' && studentsDropdownOpen) || (item.key === 'company' && companyDropdownOpen) || (item.key === 'about' && aboutDropdownOpen)) && (
            <ul className="mt-2 ml-4 space-y-1">
              {item.subItems.map((subItem) => (
                <li key={subItem.key}>
                  <button
                    onClick={() => handleSubMenuClick(subItem.href)}
                    className={`
                      w-full flex items-center px-4 py-2 text-sm rounded-lg transition-colors duration-200
                      ${location.pathname === subItem.href
                        ? 'bg-primary-400 text-white'
                        : 'text-gray-400 hover:bg-secondary-800 hover:text-white'
                      }
                    `}
                  >
                    {subItem.label}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <button
          onClick={() => handleMenuClick(item.key, item.href)}
          className={`
            w-full flex items-center px-2 py-3 text-sm font-medium rounded-lg transition-colors duration-200
            ${getActiveMenu() === item.key
              ? 'bg-primary-500 text-white shadow-md'
              : 'text-gray-300 hover:bg-secondary-800 hover:text-white'
            }
          `}
        >
          <span className="mr-3">{item.icon}</span>
          {item.label}
        </button>
      )}
    </li>
  );

  return (
    <>
      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 h-screen overflow-y-auto overflow-x-hidden w-64 bg-secondary-900 transform transition-transform duration-300 ease-in-out
        lg:translate-x-0 lg:static lg:inset-0
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Sidebar Header */}
        <div className="flex items-center justify-between h-20 px-4 bg-secondary-900 border-b border-secondary-800">
          <div className="flex items-center w-full space-x-2 py-8">
            <div className="max-h-6 flex items-center justify-center w-full">
              <img src={logo} alt="Lynx Logo" className="object-contain max-h-10" />
            </div>
          </div>
          
          {/* Mobile close button */}
          <button
            onClick={onClose}
            className="lg:hidden text-gray-300 hover:text-white"
          >
            <FiX className="h-6 w-6" />
          </button>
        </div>

        {/* Navigation Menu */}
        <nav className="mt-8 px-4">
          <ul className="space-y-2">
            {/* Main Navigation Section */}
            {renderMenuItem(menuItems[0], 0, false)} {/* Dashboard */}
            
            {/* Content Management Section */}
            {/* <span className="text-gray-300 text-sm font-medium">Events & Subscriptions Management</span> */}
            {menuItems.slice(1, 10).map((item, index) => 
              renderMenuItem(item, index + 1, false)
            )}
            
            {/* System Management Section */}
            <div className="border-t border-secondary-700 my-3"></div>
            <h3 className="text-primary-500 text-sm font-medium p-4">Web Pages Management</h3>
            {menuItems.slice(10).map((item, index) => 
              renderMenuItem(item, index + 10, false)
            )}
          </ul>
        </nav>

        {/* Sidebar Footer */}
        <div className="bg-secondary-900 bottom-0 left-0 right-0 p-4 border-t border-secondary-800">
          <div className="flex items-center justify-between text-gray-300">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-medium">
                  {user?.name?.charAt(0) || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {user?.name || 'User'}
                </p>
                <p className="text-xs text-gray-400 truncate">
                  {user?.email || 'user@example.com'}
                </p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 text-gray-300 hover:text-white hover:bg-secondary-800 rounded-lg transition-colors duration-200"
              title="Logout"
            >
              <FiLogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={onClose}
        />
      )}
    </>
  );
};

export default Sidebar; 